import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../tokens';

const inputSizes = {
  sm: css`
    height: 36px;
    padding: 0 ${theme.spacing[3]};
    font-size: ${theme.fontSizes.sm};
  `,
  md: css`
    height: 44px;
    padding: 0 ${theme.spacing[4]};
    font-size: ${theme.fontSizes.base};
  `,
  lg: css`
    height: 52px;
    padding: 0 ${theme.spacing[5]};
    font-size: ${theme.fontSizes.lg};
  `,
};

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1.5]};
  width: 100%;
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};

  ${({ $required }) =>
    $required &&
    css`
      &::after {
        content: ' *';
        color: ${theme.colors.error[500]};
      }
    `}
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  width: 100%;
  border: 1px solid ${theme.colors.border.default};
  border-radius: ${theme.radii.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-family: ${theme.fontFamilies.sans};
  transition: ${theme.transitions.input};
  outline: none;

  ${({ $size }) => inputSizes[$size] || inputSizes.md}

  ${({ $hasLeftIcon }) =>
    $hasLeftIcon &&
    css`
      padding-left: ${theme.spacing[10]};
    `}

  ${({ $hasRightIcon }) =>
    $hasRightIcon &&
    css`
      padding-right: ${theme.spacing[10]};
    `}

  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: ${theme.colors.border.dark};
  }

  &:focus {
    border-color: ${theme.colors.border.focus};
    box-shadow: ${theme.shadows.focus};
  }

  &:disabled {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.disabled};
    cursor: not-allowed;
  }

  ${({ $error }) =>
    $error &&
    css`
      border-color: ${theme.colors.error[500]};

      &:focus {
        border-color: ${theme.colors.error[500]};
        box-shadow: ${theme.shadows.focusError};
      }
    `}

  ${({ $success }) =>
    $success &&
    css`
      border-color: ${theme.colors.success[500]};

      &:focus {
        border-color: ${theme.colors.success[500]};
        box-shadow: ${theme.shadows.focusSuccess};
      }
    `}
`;

const IconWrapper = styled.span`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.tertiary};
  pointer-events: none;

  ${({ $position }) =>
    $position === 'left'
      ? css`
          left: ${theme.spacing[3]};
        `
      : css`
          right: ${theme.spacing[3]};
        `}

  svg {
    width: 20px;
    height: 20px;
  }
`;

const HelperText = styled.span`
  font-size: ${theme.fontSizes.xs};
  color: ${({ $error, $success }) =>
    $error ? theme.colors.error[500] : $success ? theme.colors.success[500] : theme.colors.text.secondary};
`;

const CharCount = styled.span`
  font-size: ${theme.fontSizes.xs};
  color: ${({ $exceeded }) => ($exceeded ? theme.colors.error[500] : theme.colors.text.tertiary)};
  margin-left: auto;
`;

const HelperRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

export const Input = React.forwardRef(
  (
    {
      label,
      helperText,
      error,
      success,
      size = 'md',
      leftIcon,
      rightIcon,
      required,
      maxLength,
      showCharCount,
      value,
      className,
      wrapperClassName,
      ...props
    },
    ref
  ) => {
    const charCount = typeof value === 'string' ? value.length : 0;
    const exceeded = maxLength && charCount > maxLength;

    return (
      <InputWrapper className={wrapperClassName}>
        {label && <Label $required={required}>{label}</Label>}
        <InputContainer>
          {leftIcon && <IconWrapper $position="left">{leftIcon}</IconWrapper>}
          <StyledInput
            ref={ref}
            $size={size}
            $error={error || exceeded}
            $success={success}
            $hasLeftIcon={!!leftIcon}
            $hasRightIcon={!!rightIcon}
            value={value}
            maxLength={maxLength}
            className={className}
            {...props}
          />
          {rightIcon && <IconWrapper $position="right">{rightIcon}</IconWrapper>}
        </InputContainer>
        {(helperText || showCharCount) && (
          <HelperRow>
            {helperText && (
              <HelperText $error={error} $success={success}>
                {helperText}
              </HelperText>
            )}
            {showCharCount && maxLength && (
              <CharCount $exceeded={exceeded}>
                {charCount}/{maxLength}
              </CharCount>
            )}
          </HelperRow>
        )}
      </InputWrapper>
    );
  }
);

Input.displayName = 'Input';

export default Input;
